import { EntityId, Update, createAsyncThunk } from '@reduxjs/toolkit';
import { Page, pagesSlice } from './pagesSlice';
import { RootState } from '../..';
import isUndefined from 'lodash/isUndefined';
import { Change } from '../../change.type';

const { hide, show, split, splitAll, mergeAll, setFilename, restore, setUserFilename, insert } = pagesSlice.actions;

const splitPages = async ({ pages: { ids, entities } }: RootState) => {
    const result: Page[][] = [];
    let index = 0;
    ids.forEach(id => {
        const page = entities[id]!;
        const split = page.split.new ?? page.split.old;

        if (!result[index]) result.push([]);

        result[index].push(page);

        if (split) ++index;
    });

    return result;
};

const valueIsModified = <T>(valDesc: Change<T>) => !isUndefined(valDesc.new) && valDesc.new !== valDesc.old;

export const checkIsPageModified = ({ hidden, split, index, userFileName }: Page) =>
    valueIsModified(index) || valueIsModified(split) || hidden || userFileName;
export const checkIsPageModifiedIndexOrSplit = ({ split, index, userFileName }: Page) =>
    valueIsModified(index) || valueIsModified(split) || userFileName;

const checkRange = (pages: Page[]) => pages.some(p => isUndefined(p.sourceFileRefId) || checkIsPageModified(p));
export const getPageRange = async (state: RootState) => {
    const rangePages = await splitPages(state);
    return rangePages.filter(r => r[0].pageNr > 1 || checkRange(r));
};

const generateFilenamesUpdate = async (state: RootState) => {
    const splitedPages = await splitPages(state);
    const updatedPages: Update<Page>[] = [];
    const filenameCounterDict: { [filename: string]: Page[][] } = {};

    splitedPages.forEach(page => {
        const filename = page[0].fileName.old!;
        if (!filenameCounterDict[filename]) filenameCounterDict[filename] = [];
        filenameCounterDict[filename].push(page);
    });

    for (const key in filenameCounterDict) {
        const pageRangeArr = filenameCounterDict[key];
        if (pageRangeArr.length > 1) {
            pageRangeArr.forEach((p, idx) => {
                const firstPage = p[0];
                const filename = firstPage.fileName.old!.split('.').slice(0, -1).join('.');
                const newFilename = `${filename}(${idx + 1}).pdf`;

                updatedPages.push({
                    id: firstPage.id,
                    changes: {
                        fileName: {
                            old: firstPage.fileName.old,
                            new: newFilename
                        }
                    }
                });
            });
        } else {
            const range = pageRangeArr[0];
            const { id, fileName } = range[0];
            const modified =
                range.some(checkIsPageModified) ||
                range.length !== state.files.entities[range[0].sourceFileId]!.pagesIds.length;

            updatedPages.push({
                id,
                changes: {
                    fileName: {
                        old: fileName.old,
                        new: modified ? `${fileName.old!.split('.').slice(0, -1).join('.')}(1).pdf` : undefined
                    }
                }
            });
        }
    }

    return updatedPages;
};

export const splitAction = createAsyncThunk('pages/splitPage', async (id: EntityId, thunkAPI) => {
    thunkAPI.dispatch(split(id));
    const state = thunkAPI.getState() as RootState;
    const pagesToUpdate = await generateFilenamesUpdate(state);
    thunkAPI.dispatch(setFilename(pagesToUpdate));
});

export const splitAllAction = createAsyncThunk('pages/splitAllPage', async (_, thunkAPI) => {
    thunkAPI.dispatch(splitAll());
    const state = thunkAPI.getState() as RootState;
    const pagesToUpdate = await generateFilenamesUpdate(state);
    thunkAPI.dispatch(setFilename(pagesToUpdate));
});

export const mergeAllAction = createAsyncThunk('pages/mergeAllPage', async (_, thunkAPI) => {
    thunkAPI.dispatch(mergeAll());
    const state = thunkAPI.getState() as RootState;
    const pagesToUpdate = await generateFilenamesUpdate(state);
    thunkAPI.dispatch(setFilename(pagesToUpdate));
});

export const insertAfterPageAction = createAsyncThunk(
    'pages/insertAfterPage',
    async (payload: { pageId: EntityId; insertAfterPageId: EntityId }, thunkAPI) => {
        thunkAPI.dispatch(insert(payload));
        const state = thunkAPI.getState() as RootState;
        const pagesToUpdate = await generateFilenamesUpdate(state);
        thunkAPI.dispatch(setFilename(pagesToUpdate));
    }
);
export const hidePageAction = createAsyncThunk('pages/hidePage', async (id: EntityId, thunkAPI) => {
    thunkAPI.dispatch(hide(id));
    const state = thunkAPI.getState() as RootState;
    const pagesToUpdate = await generateFilenamesUpdate(state);
    thunkAPI.dispatch(setFilename(pagesToUpdate));
});
export const showPageAction = createAsyncThunk('pages/showPage', async (id: EntityId, thunkAPI) => {
    thunkAPI.dispatch(show(id));
    const state = thunkAPI.getState() as RootState;
    const pagesToUpdate = await generateFilenamesUpdate(state);
    thunkAPI.dispatch(setFilename(pagesToUpdate));
});

// export const hidePageAction = hide;
// export const showPageAction = show;
export const setFilenameAction = setFilename;
export const restoreDefaultAction = restore;
export const setUserFilenameAction = setUserFilename;
// export const insertAfterPageAction = insert;
// export const splitAllPagesAction = splitAll;
